import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { AttributeFilterModel, AttributeValueSettings, FilterToolEnum } from '@tailormap-viewer/api';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterHelper, TooltipDirective } from '@tailormap-viewer/shared';
import { MatChipSet, MatChip, MatChipRemove } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'tm-dropdown-list-filter',
    templateUrl: './dropdown-list-filter.component.html',
    styleUrls: ['./dropdown-list-filter.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatChipSet,
        MatChip,
        MatChipRemove,
        MatIcon,
        MatFormField,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatOption,
        TooltipDirective,
        AsyncPipe,
    ],
})
export class DropdownListFilterComponent implements OnInit {
  private destroyRef = inject(DestroyRef);


  @Input()
  public set dropdownListFilter(filter: AttributeFilterModel) {
    const editConfiguration = filter.editConfiguration;
    if (editConfiguration?.filterTool !== FilterToolEnum.DROPDOWN_LIST) {
      return;
    }
    this.attributeValueSettingsSubject$.next(editConfiguration.attributeValuesSettings || []);
    const selectedValues: { value: string; alias?: string }[] = filter.value.map(value => {
      const alias = editConfiguration.attributeValuesSettings.find(s => s.value === value)?.alias;
      return { value, alias };
    });
    this.selectedValuesSubject$.next(selectedValues);
  }

  @Input()
  public set uniqueValues(uniqueValues: string[] | null) {
    this.allUniqueValuesSubject$.next(uniqueValues);
  }

  @Output()
  public valueSelected = new EventEmitter<{ value: string; selected: boolean }>();

  public filter = new FormControl<string | { value: string; alias?: string }[]>('');
  private filterSubject$ = new BehaviorSubject<string | null>(null);
  private allUniqueValuesSubject$ = new BehaviorSubject<string[] | null>(null);
  private attributeValueSettingsSubject$ = new BehaviorSubject<AttributeValueSettings[]>([]);
  public selectedValuesSubject$ = new BehaviorSubject<{ value: string; alias?: string }[]>([]);

  private uniqueValuesForFilter$: Observable<{ value: string; alias?: string }[]> = of([]);
  public selectableUniqueValues$: Observable<{ value: string; alias?: string }[]> = of([]);

  public ngOnInit(): void {
    this.filter.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (typeof value === 'string') {
          this.filterSubject$.next(value);
        } else if (Array.isArray(value)) {
          this.filterSubject$.next('');
        }
      });

    this.uniqueValuesForFilter$ = combineLatest([
      this.allUniqueValuesSubject$.asObservable(),
      this.attributeValueSettingsSubject$.asObservable(),
    ]).pipe(
      map(([ allUniqueValues, attributeValueSettings ]) => {
        if (!allUniqueValues) {
          return [];
        }
        return allUniqueValues.filter(value => {
          const alteredValue = attributeValueSettings.find(av => av.value === value);
          return !(alteredValue && !alteredValue.selectable);
        }).map(value => {
            const alteredValue = attributeValueSettings.find(av => av.value === value);
            if (alteredValue) {
              return {
                value: value,
                alias: alteredValue.alias,
              };
            }
            return { value: value };
        });
      }),
    );

    this.selectableUniqueValues$ = combineLatest([
      this.filterSubject$.asObservable(),
      this.uniqueValuesForFilter$,
      this.selectedValuesSubject$.asObservable(),
    ]).pipe(map(([ filter, uniqueValues, selectedValues ]) => {
      if (!uniqueValues) {
        return [];
      }
      const selectedValuesValues = selectedValues.map(v => v.value);
      const uniqueValuesWithoutSelected = uniqueValues.filter(value =>
        !selectedValuesValues.includes(value.value));
      if (filter && uniqueValuesWithoutSelected) {
        return FilterHelper.filterByTerm(uniqueValuesWithoutSelected, filter, value => value.alias || value.value);
      }
      return uniqueValuesWithoutSelected ? uniqueValuesWithoutSelected : [];
    }));
  }

  public valueClicked(value: { value: string; alias?: string }) {
    const selectedValues = this.selectedValuesSubject$.getValue();
    this.selectedValuesSubject$.next([ ...selectedValues, value ]);
    this.valueSelected.emit({ value: value.value, selected: true });
    this.filter.setValue('');
  }

  public displayFn(value: { value: string; alias?: string }): string {
    return value.alias || value.value;
  }

  public removeValue(value: string) {
    const selectedValues = this.selectedValuesSubject$.getValue();
    const newSelectedValues = selectedValues.filter(v => v.value !== value);
    this.selectedValuesSubject$.next(newSelectedValues);
    this.valueSelected.emit({ value: value, selected: false });
  }
}
